import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { contactInfo, cities } from '../mockData';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you shortly.",
    });
    setFormData({ name: '', email: '', phone: '', city: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2E3C5B]">
            Get in Touch
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Have questions? We're here to help with all your laptop needs
          </p>
          <div className="w-24 h-1 bg-[#E94B3C] mx-auto rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-[#2E3C5B] mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start gap-4 group cursor-pointer">
                  <div className="w-12 h-12 bg-[#E94B3C] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Phone className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3C5B] mb-1">Call Us</h4>
                    <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-[#E94B3C] transition-colors">
                      {contactInfo.phone}
                    </a>
                    <br />
                    <a href={`tel:${contactInfo.alternatePhone}`} className="text-gray-600 hover:text-[#E94B3C] transition-colors">
                      {contactInfo.alternatePhone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4 group cursor-pointer">
                  <div className="w-12 h-12 bg-[#E94B3C] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Mail className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3C5B] mb-1">Email Us</h4>
                    <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-[#E94B3C] transition-colors">
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4 group cursor-pointer">
                  <div className="w-12 h-12 bg-[#E94B3C] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3C5B] mb-1">Visit Us</h4>
                    <p className="text-gray-600">{contactInfo.address}</p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4 group cursor-pointer">
                  <div className="w-12 h-12 bg-[#E94B3C] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Clock className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3C5B] mb-1">Business Hours</h4>
                    <p className="text-gray-600">Mon - Sat: 9:00 AM - 8:00 PM</p>
                    <p className="text-gray-600">Sunday: 10:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cities */}
            <div>
              <h3 className="text-2xl font-bold text-[#2E3C5B] mb-6">Our Locations</h3>
              <div className="grid grid-cols-2 gap-4">
                {cities.map((city, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-[#2E3C5B] to-[#1f2a3f] rounded-lg p-4 text-white hover:scale-105 transition-transform cursor-pointer"
                  >
                    <h4 className="font-bold text-lg">{city.name}</h4>
                    <p className="text-sm text-gray-200">{city.branches} Branches</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
            <h3 className="text-2xl font-bold text-[#2E3C5B] mb-6">Send us a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-[#2E3C5B] mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your name"
                  className="border-gray-300 focus:border-[#E94B3C] focus:ring-[#E94B3C]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2E3C5B] mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className="border-gray-300 focus:border-[#E94B3C] focus:ring-[#E94B3C]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2E3C5B] mb-2">
                  Phone Number *
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+91 XXXXX XXXXX"
                  className="border-gray-300 focus:border-[#E94B3C] focus:ring-[#E94B3C]"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2E3C5B] mb-2">
                  City
                </label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-[#E94B3C] focus:ring-1 focus:ring-[#E94B3C]"
                >
                  <option value="">Select a city</option>
                  {cities.map((city, index) => (
                    <option key={index} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2E3C5B] mb-2">
                  Message *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Tell us how we can help you..."
                  className="border-gray-300 focus:border-[#E94B3C] focus:ring-[#E94B3C]"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-[#E94B3C] hover:bg-[#d43d2c] text-white border-0 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 group"
              >
                Send Message
                <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
